import React, { useState, useEffect } from "react"
import * as uuid from "uuid"
import * as layouts from "layouts"
import * as inputs from "inputs"
import * as icons from "icons"
import * as errors from "errors"
import * as httpx from "httpx"
import * as brands from "brands"
import * as authzc from "authzcached"
import * as api from "brandguard/api"
import * as profiles from "profiles"
import * as training from "brandguard/display/training"

interface props extends layouts.containers.FlexProps {
	refreshTrigger?: number
}

export default function ProductTaxonomy(props: React.PropsWithChildren<props>): JSX.Element {
	const widthProps = { width: "12ch" }
	const { refreshTrigger, ...rest } = props
	const brand = brands.caching.useCached()
	const authzaccount = authzc.useCache((cached) => cached.meta)

	const [items, setItems] = useState<api.ProductTaxonomy[]>([])
	const [loading, setLoading] = useState(true)
	const [cause, setCause] = useState(undefined as errors.Cause)

	const [productNames, setProductNames] = useState("")
	const [category, setCategory] = useState("")
	const [teamOwner, setTeamOwner] = useState("")
	const [reset, setReset] = useState(uuid.v4())
	const [editingId, setEditingId] = useState<string | null>(null)

	const [users, setUsers] = useState<profiles.api.proto.Profile[]>([])

	const parseProductNames = (input: string): { name: string; aliases: string[] } => {
		const parts = input
			.split(",")
			.map((p) => p.trim())
			.filter((p) => p.length > 0)
		if (parts.length === 0) return { name: "", aliases: [] }
		return { name: parts[0], aliases: parts.slice(1) }
	}

	const { name, aliases } = parseProductNames(productNames)

	const disabled = inputs.assertions.failed(
		inputs.assertions.strings.min(1)(name),
		inputs.assertions.strings.max(256)(name),
	)

	const resetForm = () => {
		setProductNames("")
		setCategory("")
		setTeamOwner("")
		setEditingId(null)
		setReset(uuid.v4())
	}

	const loadTaxonomy = () => {
		setLoading(true)
		const retry = httpx.autoretry()
		retry
			.wrap(() =>
				api.taxonomy.search(
					api.taxonomy.searches.request({ brand_id: brand.id, content_type: "document" }),
					authzc.bearer(authzaccount),
				),
			)
			.then((r) => {
				setItems(r.items || [])
				setLoading(false)
				setCause(undefined)
			})
			.catch((cause) => {
				console.warn(cause)
				setCause(
					<errors.Display cause={cause} onClick={() => setCause(undefined)}>
						failed to load taxonomy
					</errors.Display>,
				)
				setLoading(false)
			})
	}

	const loadUsers = () => {
		const retry = httpx.autoretry()
		retry
			.wrap(() =>
				profiles.api.search(
					profiles.api.searches.zero({ enabled: profiles.api.StatusFilters.Enabled, limit: 100n }),
					authzc.bearer(authzaccount),
				),
			)
			.then((r) => {
				setUsers(r.items || [])
			})
			.catch((cause) => {
				console.warn("failed to load users", cause)
			})
	}

	useEffect(() => {
		loadTaxonomy()
		loadUsers()
	}, [brand.id, refreshTrigger])

	const handleAdd = () => {
		setLoading(true)
		const retry = httpx.autoretry()
		retry
			.wrap(() =>
				api.taxonomy.create(
					{
						brand_id: brand.id,
						content_type: "document",
						name,
						aliases,
						category,
						team_owner: teamOwner,
					},
					authzc.bearer(authzaccount),
				),
			)
			.then((r) => {
				setItems([...items, r])
				resetForm()
				setLoading(false)
				setCause(undefined)
			})
			.catch((cause) => {
				console.warn(cause)
				setCause(
					<errors.Display cause={cause} onClick={() => setCause(undefined)}>
						failed to create taxonomy entry
					</errors.Display>,
				)
				setLoading(false)
			})
	}

	const handleUpdate = () => {
		if (!editingId) return

		setLoading(true)
		const retry = httpx.autoretry()
		retry
			.wrap(() =>
				api.taxonomy.update(
					editingId,
					{
						id: editingId,
						name,
						aliases,
						category,
						team_owner: teamOwner,
					},
					authzc.bearer(authzaccount),
				),
			)
			.then((r) => {
				setItems(items.map((item) => (item.id === editingId ? r : item)))
				resetForm()
				setLoading(false)
				setCause(undefined)
			})
			.catch((cause) => {
				console.warn(cause)
				setCause(
					<errors.Display cause={cause} onClick={() => setCause(undefined)}>
						failed to update taxonomy entry
					</errors.Display>,
				)
				setLoading(false)
			})
	}

	const handleDelete = (id: string) => {
		setLoading(true)
		const retry = httpx.autoretry()
		retry
			.wrap(() => api.taxonomy.remove(id, authzc.bearer(authzaccount)))
			.then(() => {
				setItems(items.filter((item) => item.id !== id))
				setLoading(false)
				setCause(undefined)
			})
			.catch((cause) => {
				console.warn(cause)
				setCause(
					<errors.Display cause={cause} onClick={() => setCause(undefined)}>
						failed to delete taxonomy entry
					</errors.Display>,
				)
				setLoading(false)
			})
	}

	const startEdit = (item: api.ProductTaxonomy) => {
		setEditingId(item.id)
		const allNames = [item.name, ...(item.aliases || [])].join(", ")
		setProductNames(allNames)
		setCategory(item.category)
		setTeamOwner(item.team_owner)
		setReset(uuid.v4())
	}

	const getUserDisplay = (userId: string): string => {
		const user = users.find((u) => u.id === userId)
		return user ? (user.display || user.email) : userId
	}

	const taxonomyItems = items.map((item) => (
		<layouts.Flex
			key={item.id}
			py="10px"
			flexDirection="column"
			borderBottom={layouts.theme.borders.grey.dark50alphamedium1px}
		>
			<layouts.Flex alignItems="center">
				<layouts.Flex flexDirection="column" flex="1">
					<layouts.Flex>
						<layouts.Span fontWeight="600" {...widthProps}>
							Product:
						</layouts.Span>
						<layouts.Span>
							{[item.name, ...(item.aliases || [])].join(", ")}
						</layouts.Span>
					</layouts.Flex>
					{item.category && (
						<layouts.Flex>
							<layouts.Span {...widthProps}>Category:</layouts.Span>
							<layouts.Span>{item.category}</layouts.Span>
						</layouts.Flex>
					)}
					{item.team_owner && (
						<layouts.Flex>
							<layouts.Span {...widthProps}>Owner:</layouts.Span>
							<layouts.Span>{getUserDisplay(item.team_owner)}</layouts.Span>
						</layouts.Flex>
					)}
				</layouts.Flex>
				<layouts.Flex gridGap="10px">
					<layouts.Span
						cursor="pointer"
						onClick={(evt) => {
							evt.stopPropagation()
							startEdit(item)
						}}
					>
						<icons.Edit width="14px" height="14px" />
					</layouts.Span>
					<layouts.Span
						cursor="pointer"
						onClick={(evt) => {
							evt.stopPropagation()
							handleDelete(item.id)
						}}
					>
						<icons.Trash width="14px" height="14px" />
					</layouts.Span>
				</layouts.Flex>
			</layouts.Flex>
		</layouts.Flex>
	))

	return (
		<layouts.Flex flexDirection="column" flex="1" {...rest}>
			{cause}
			<layouts.forms.Container key={reset} p="10px">
				<layouts.forms.Group>
					<layouts.forms.Label {...widthProps}>Product Name</layouts.forms.Label>
					<inputs.Text
						autoFocus
						placeholder="Product, Alias 1, Alias 2, ..."
						defaultValue={productNames}
						onChange={(evt) => setProductNames(evt.target.value)}
						className={training.layouts.styledTextInput}
					/>
				</layouts.forms.Group>
				<layouts.forms.Group>
					<layouts.forms.Label {...widthProps}>Category</layouts.forms.Label>
					<inputs.Text
						placeholder="e.g., Hardware, Software, Service"
						defaultValue={category}
						onChange={(evt) => setCategory(evt.target.value)}
						className={training.layouts.styledTextInput}
					/>
				</layouts.forms.Group>
				<layouts.forms.Group>
					<layouts.forms.Label {...widthProps}>Owner</layouts.forms.Label>
					<inputs.Select
						value={teamOwner}
						onChange={(evt) => setTeamOwner(evt.target.value)}
						style={{
							flex: 1,
							height: "40px",
							borderRadius: "5px",
							padding: "0 10px",
							border: `1px solid ${layouts.theme.colors.grey.dark50alpha10}`,
						}}
					>
						<option value="">Select an owner...</option>
						{users.map((user) => (
							<option key={user.id} value={user.id}>
								{user.display || user.email}
							</option>
						))}
					</inputs.Select>
				</layouts.forms.Group>
				<layouts.Flex flex="1" flexDirection="row" justifyContent="space-between" mt="10px">
					<layouts.buttons.outline onClick={() => resetForm()} width="10ch" borderRadius="5px">
						Cancel
					</layouts.buttons.outline>
					<layouts.buttons.primary
						width="10ch"
						borderRadius="5px"
						disabled={disabled}
						onClick={() => {
							if (editingId) {
								handleUpdate()
							} else {
								handleAdd()
							}
						}}
					>
						{editingId ? "Update" : "Add"}
					</layouts.buttons.primary>
				</layouts.Flex>
			</layouts.forms.Container>
			<layouts.Flex flexDirection="column" mt="10px">
				{items.length === 0 && !loading && (
					<layouts.Flex py="20px" justifyContent="center" color={layouts.theme.colors.grey.dark50}>
						No products defined. Add products to enable product detection.
					</layouts.Flex>
				)}
				{taxonomyItems}
			</layouts.Flex>
		</layouts.Flex>
	)
}
